import { createFileRoute, redirect } from '@tanstack/react-router';
import { lazy, Suspense } from 'react';
import { z } from 'zod';
import { CommunityFeedSkeleton } from '@/components/community/CommunitySkeletons';
import { requireAlfieCapability } from '@/api/alfie-plan';
import { isSpeediesEnabled } from '@/lib/feature-flags';
import { communitySearchSchema } from '@/lib/community-search';

const Community = lazy(() => import('@/pages/Community'));

/** The keys the chat view reads (the same ones `chatSearchFrom` carries). */
const chatSearchSchema = communitySearchSchema.pick({
  filter: true,
  community: true,
  conversation: true,
  message: true,
});

export type ChatSearchParams = z.infer<typeof chatSearchSchema>;

/**
 * Chat as its own page (Speedies, F-129). It is the Community page's chat view, rendered in chat
 * mode, so no chat behaviour changes. With the flag off, /chat goes to /community?tab=chats.
 */
export const Route = createFileRoute('/chat/')({
  beforeLoad: async ({ context: { queryClient }, search }) => {
    if (!isSpeediesEnabled()) {
      throw redirect({ to: '/community', search: { ...search, tab: 'chats' }, replace: true });
    }
    await requireAlfieCapability(queryClient, 'community');
  },
  validateSearch: (search: Record<string, unknown>): ChatSearchParams => chatSearchSchema.parse(search),
  pendingComponent: CommunityFeedSkeleton,
  component: () => (
    <Suspense fallback={<CommunityFeedSkeleton />}>
      <Community mode="chat" />
    </Suspense>
  ),
});
